import React, { useState, useEffect } from 'react';
import { FileUploader } from "react-drag-drop-files";
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Grid,
  styled,
  IconButton,
  LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getBoxAccessToken, uploadAssetToBox } from '../../apis/apiList/boxAssetsApi';

// Custom styled components
const UploadContainer = styled(Paper)(({ theme }) => ({
  border: '1px dashed #90CAF9',
  borderRadius: 4,
  backgroundColor: '#F5FAFF',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  minHeight: 80,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#E3F2FD',
  }
}));

const BetaRibbon = styled(Box)({
  position: 'absolute',
  top: 0,
  right: 0,
  overflow: 'hidden',
  width: 100,
  height: 100,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '150%',
    height: '40px',
    backgroundColor: '#D32F2F',
    transform: 'rotate(45deg) translateX(20%) translateY(-50%)',
    transformOrigin: 'top right',
    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
  },
  '&::after': {
    content: '"BETA"',
    position: 'absolute',
    top: '12px',
    right: '5px',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '18px',
    transform: 'rotate(45deg)',
    zIndex: 1,
  }
});

const ImageThumbnail = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 48,
  height: 48,
  borderRadius: 4,
  overflow: 'hidden',
  marginRight: 8,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  '& .remove-icon': {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '50%',
    padding: 2,
    zIndex: 1,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
  }
}));

const fileTypes = ["JPG", "JPEG", "PNG", "GIF", "WEBP", "MP3", "WAV", "MP4", "MOV", "AVI"];

function FileUploaderComponent({openFolderId, handleUploadedFiles}) {
  const [files, setFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(false);

  useEffect(() => {
    // Generate previews whenever files change
    if (files.length > 0) {
      const newPreviews = [];
      
      files.forEach(file => {
        // Only create previews for image files
        if (file.type.startsWith('image/')) {
          const objectUrl = URL.createObjectURL(file);
          newPreviews.push({ file, url: objectUrl });
        } else {
          // For non-image files, use a placeholder icon
          newPreviews.push({ 
            file, 
            url: null,
            isDocument: true 
          });
        }
      });
      
      setFilePreviews(newPreviews);
      
      // Clean up object URLs on unmount
      return () => {
        newPreviews.forEach(preview => {
          if (preview.url) URL.revokeObjectURL(preview.url);
        });
      };
    }
  }, [files]);

  const handleChange = (newFiles) => {
    // Convert FileList to array if multiple files
    if (newFiles instanceof FileList || Array.isArray(newFiles)) {
        let Files = [...files, ...newFiles]
        setFiles(Files);
    //   setFiles(Array.from(newFiles));
    } else {
      // Handle single file case
      setFiles([newFiles]);
    }
  };

  const handleRemoveFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const removeDuplicates = (array, key) => {
    return array.filter((obj, index, self) =>
        index === self.findIndex((o) => o[key] === obj[key])
    );
  };
  // Helper function to render default icon for non-image files
  const renderFileIcon = (fileType) => {
    return (
      <Box sx={{ 
        width: '100%', 
        height: '100%', 
        bgcolor: '#e0e0e0', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontSize: '10px',
        fontWeight: 'bold'
      }}>
        {fileType.split('/')[1]?.toUpperCase() || 'FILE'}
      </Box>
    );
  };

  const handleSubmit =async ()=>{
    // console.log('handleSubmit', files)
    setIsUploading(true);
    setUploadProgress(0);
    setLoadingMessage('File is uploading...');
    let uploadFile = []
    try {
      const result = await getBoxAccessToken();
      // console.log('result=', result);
      
      let uniqueFiles = removeDuplicates(files, "name");
      const totalFiles = uniqueFiles.length;
      // console.log('uniqueFiles', uniqueFiles)
      let isError = false;
      for (let i = 0; i < totalFiles; i++) {
          let filecount = i + 1;
          setLoadingMessage(`Uploading file ${filecount} of ${totalFiles}`);

          let date_ob_new = Date.now();
          let file = uniqueFiles[i].name.split(/\.(?=[^\.]+$)/);
          let newFileName = `${file[0]}_${date_ob_new}.${file[1]}`;
          
          let formData = new FormData();
          formData.append('file', uniqueFiles[i], newFileName);
          formData.append('parent_id', openFolderId);
          
          const response = await fetch('https://upload.box.com/api/2.0/files/content', {
              method: 'POST',
              headers: {
                  Authorization: `Bearer ${result.data.access_token}`,
              },
              body: formData,
          });
          const responseData = await response.json(); // Read response body

          // console.log('File uploaded successfully:', responseData);
          
          if (response.status === 201) {
              // console.log('File uploaded successfully:', response);
              // console.log('All file uploaded successfully.');
              
              let boxdata={
                  filename: newFileName,
                  parent_id: openFolderId,
                  fileId: responseData.entries[0].id,
                  size: responseData.entries[0].size
              }
              
              const bresult = await uploadAssetToBox(boxdata);

              isError= false
              // console.log('bresult=',bresult);
              uploadFile.push(bresult.data);
              setUploadProgress(((i + 1) / totalFiles) * 100);
          } else {
              console.error('Error uploading file');
              isError= true
          }
      }
      if(isError){
        // console.log('in if files not uploaded')
        setLoadingMessage('Error uploading file..!');
      } else {
        // console.log('in else files uploaded')
        handleUploadedFiles(openFolderId, uploadFile)
        setFilePreviews([]);
        setFiles([]);
        setLoadingMessage('All files uploaded successfully!');
      }
      
    } catch (error) {
          console.error('Upload error:', error);
          setLoadingMessage('Error during file upload.');
      }
  
      setTimeout(() => {
          setIsUploading(false);
          setUploadProgress(0);
      }, 3000);
  }
  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: '1000px', margin: '0 auto'}}>
      {isUploading && (
          <Box sx={{ width: '100%', my: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" align="center">{loadingMessage}</Typography>
          </Box>
      )}
      <Grid container spacing={2}>
        {/* File Uploader Section */}
        <Grid item xs={12} md={6}>
          {files.length === 0 ? (
            <FileUploader
              handleChange={handleChange}
              name="file"
              types={fileTypes}
              multiple
              dropMessageStyle={{ display: 'flex', justifyContent: 'center' }}
            >
              <UploadContainer>
                <Typography  align="center">
                  Drag Files or Click to Browse
                </Typography>
              </UploadContainer>
            </FileUploader>
          ) : (
            <UploadContainer>
              <Box sx={{ width: '100%', mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {filePreviews.map((preview, index) => (
                    <ImageThumbnail key={index}>
                      {preview.url ? (
                        <img src={preview.url} alt={`Preview ${index}`} />
                      ) : (
                        renderFileIcon(preview.file.type)
                      )}
                      <IconButton 
                        className="remove-icon" 
                        size="small"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </ImageThumbnail>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FileUploader
                    handleChange={handleChange}
                    name="file"
                    types={fileTypes}
                    multiple
                  >
                    <Button variant="contained" color="primary">
                      ADD FILES
                    </Button>
                  </FileUploader>
                  <Button variant="contained" color="error" onClick={handleSubmit}>
                    SUBMIT
                  </Button>
                </Box>
              </Box>
            </UploadContainer>
          )}
        </Grid>

        {/* Video Record Section */}
        {/* <Grid item xs={12} md={6}>
          <UploadContainer>
            <Typography color="primary" align="center">
              Click to Record Video
            </Typography>
          </UploadContainer>
        </Grid> */}
      </Grid>

      {/* Beta Ribbon */}
      {/* <BetaRibbon /> */}
    </Box>
  );
}

export default FileUploaderComponent;